import { useEffect, useId, useMemo, useState } from "react";
import {
  Button,
  DatePicker,
  DateRangePicker,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Label,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Tag,
  cn,
} from "@jem-hr/jem-ui";
import { toast } from "sonner";
import type { DateRange } from "react-day-picker";
import {
  BarChart3,
  Download,
  ExternalLink,
  FileSpreadsheet,
  FileText,
  MapPin,
  Users as UsersIcon,
} from "lucide-react";

/* ================================================================== */
/*  Types                                                              */
/* ================================================================== */

export type ReportScope = { kind: "site"; id: string; label: string; region?: string } | {
  kind: "roster";
  id: string;
  label: string; // e.g. "JHB CBD (RD016D): SASOL SHIFT SUPERVISORS"
  parentSite?: string;
};

export type ReportType = "planned-vs-actuals" | "timesheet" | "posting-sheet";

export interface ReportTypeOption {
  id: ReportType;
  label: string;
  description: string;
  audience: string;
  output: "Excel (.xlsx)" | "PDF (.pdf)";
  icon: typeof BarChart3;
}

export const REPORT_TYPES: ReportTypeOption[] = [
  {
    id: "planned-vs-actuals",
    label: "Planned vs Actuals",
    description: "Rostered vs present, attendance %, per-site daily tiles.",
    audience: "For Operations Managers & Control Room",
    output: "Excel (.xlsx)",
    icon: BarChart3,
  },
  {
    id: "timesheet",
    label: "Timesheet",
    description: "Per-shift rows with clock times, one sheet per site plus a summary.",
    audience: "For Payroll Admins",
    output: "Excel (.xlsx)",
    icon: FileSpreadsheet,
  },
  {
    id: "posting-sheet",
    label: "Posting Sheet",
    description: "Daily guard roster with P/A/N/A status and replacement tracking.",
    audience: "For Prosec Supervisors",
    output: "PDF (.pdf)",
    icon: FileText,
  },
];

/* ================================================================== */
/*  Helpers                                                            */
/* ================================================================== */

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function formatShortDate(d: Date | undefined): string {
  if (!d) return "—";
  return `${d.getDate()} ${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
}

type DatePreset = "today" | "7d" | "30d" | "custom";

const DATE_PRESET_OPTIONS: { value: DatePreset; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "custom", label: "Custom range" },
];

function rangeForPreset(preset: DatePreset, fallback?: DateRange): DateRange | undefined {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (preset === "today") return { from: today, to: today };
  if (preset === "7d") {
    const from = new Date(today);
    from.setDate(today.getDate() - 6);
    return { from, to: today };
  }
  if (preset === "30d") {
    const from = new Date(today);
    from.setDate(today.getDate() - 29);
    return { from, to: today };
  }
  return fallback;
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function filenameFor(
  reportType: ReportType,
  scope: ReportScope,
  dateRange: DateRange | undefined,
  singleDate: Date | undefined,
  shiftType?: string,
): string {
  const scopeSlug = slugify(scope.label);
  if (reportType === "posting-sheet") {
    const d = singleDate ?? new Date();
    const datePart = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const shiftPart = shiftType === "night" ? "_night" : "_day";
    return `posting-sheet_${scopeSlug}_${datePart}${shiftPart}.pdf`;
  }
  const from = dateRange?.from ?? new Date();
  const to = dateRange?.to ?? from;
  const rangePart = `${from.getFullYear()}${String(from.getMonth() + 1).padStart(2, "0")}${String(from.getDate()).padStart(2, "0")}-${to.getFullYear()}${String(to.getMonth() + 1).padStart(2, "0")}${String(to.getDate()).padStart(2, "0")}`;
  const typeSlug = reportType === "timesheet" ? "timesheet" : "planned-vs-actuals";
  return `${typeSlug}_${scopeSlug}_${rangePart}.xlsx`;
}

/* ================================================================== */
/*  Sub-components                                                     */
/* ================================================================== */

function ScopeSummary({ scope }: { scope: ReportScope }) {
  const Icon = scope.kind === "site" ? MapPin : UsersIcon;
  const kindLabel = scope.kind === "site" ? "Site" : "Roster";
  return (
    <div className="rounded-lg border border-greyscale-border bg-neutral-50 px-md py-sm">
      <p className="text-xs font-semibold uppercase tracking-wide text-greyscale-text-caption mb-xxxs">
        Scope
      </p>
      <div className="flex items-center gap-xs flex-wrap">
        <Icon size={16} className="text-primary-brand-primary shrink-0" />
        <span className="text-sm font-semibold text-greyscale-text-title break-words min-w-0">
          {scope.label}
        </span>
        <Tag variant="drafted" size="sm">
          {kindLabel}
        </Tag>
      </div>
      {scope.kind === "site" && scope.region && (
        <p className="text-xs text-greyscale-text-caption mt-xxxs pl-[24px]">
          {scope.region}
        </p>
      )}
      {scope.kind === "roster" && scope.parentSite && (
        <p className="text-xs text-greyscale-text-caption mt-xxxs pl-[24px]">
          in {scope.parentSite}
        </p>
      )}
    </div>
  );
}

function ReportTypePicker({
  value,
  onChange,
}: {
  value: ReportType | null;
  onChange: (v: ReportType) => void;
}) {
  const idBase = useId();
  return (
    <div>
      <Label className="text-sm font-semibold text-greyscale-text-title mb-xs block">
        Report type
      </Label>
      <div className="grid grid-cols-1 gap-sm">
        {REPORT_TYPES.map((rt) => {
          const RtIcon = rt.icon;
          const selected = value === rt.id;
          const descId = `${idBase}-${rt.id}-desc`;
          const audienceId = `${idBase}-${rt.id}-audience`;
          return (
            <button
              key={rt.id}
              type="button"
              onClick={() => onChange(rt.id)}
              className={cn(
                "text-left rounded-lg border p-md transition-colors",
                selected
                  ? "border-primary-brand-primary bg-secondary-pink-50"
                  : "border-greyscale-border bg-white hover:bg-neutral-50",
              )}
              aria-pressed={selected}
              aria-describedby={`${descId} ${audienceId}`}
            >
              <div className="flex items-start gap-sm">
                <div
                  className={cn(
                    "hidden sm:block rounded-md p-xs shrink-0",
                    selected ? "bg-secondary-pink-100 text-secondary-pink-900" : "bg-neutral-100 text-greyscale-text-body",
                  )}
                >
                  <RtIcon size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-xs flex-wrap">
                    <span className="text-sm font-semibold text-greyscale-text-title">
                      {rt.label}
                    </span>
                    <Tag variant="drafted" size="sm">
                      {rt.output}
                    </Tag>
                  </div>
                  <p id={descId} className="text-xs text-greyscale-text-body mt-xxxs">
                    {rt.description}
                  </p>
                  <p id={audienceId} className="text-xs text-greyscale-text-caption mt-xxxs">
                    {rt.audience}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ShiftTypeSelector({
  value,
  onChange,
  allowAll,
}: {
  value: string;
  onChange: (v: string) => void;
  allowAll: boolean;
}) {
  return (
    <div>
      <Label className="text-sm font-semibold text-greyscale-text-title mb-xs block">
        Shift type
      </Label>
      <RadioGroup value={value} onValueChange={onChange} className="flex flex-wrap items-center gap-x-md gap-y-xs">
        <div className="flex items-center gap-xs">
          <RadioGroupItem value="day" id="rw-shift-day" />
          <Label htmlFor="rw-shift-day" className="text-sm">Day</Label>
        </div>
        <div className="flex items-center gap-xs">
          <RadioGroupItem value="night" id="rw-shift-night" />
          <Label htmlFor="rw-shift-night" className="text-sm">Night</Label>
        </div>
        {allowAll && (
          <div className="flex items-center gap-xs">
            <RadioGroupItem value="all" id="rw-shift-all" />
            <Label htmlFor="rw-shift-all" className="text-sm">All</Label>
          </div>
        )}
      </RadioGroup>
    </div>
  );
}

/* ================================================================== */
/*  Main Wizard Body                                                   */
/* ================================================================== */

export interface ReportWizardBodyProps {
  scope: ReportScope;
  /** Called when a download is triggered. Parent can choose to close the dialog. */
  onDownloadComplete?: (args: {
    reportType: ReportType;
    scope: ReportScope;
    filename?: string;
  }) => void;
  /** Provide a cancel callback to render the "Cancel" button. */
  onCancel?: () => void;
}

export function ReportWizardBody({
  scope,
  onDownloadComplete,
  onCancel,
}: ReportWizardBodyProps) {
  const [reportType, setReportType] = useState<ReportType | null>(null);
  const [datePreset, setDatePreset] = useState<DatePreset>("7d");
  const [dateRange, setDateRange] = useState<DateRange | undefined>(rangeForPreset("7d"));
  const [singleDate, setSingleDate] = useState<Date | undefined>(new Date());

  const handlePresetChange = (preset: DatePreset) => {
    setDatePreset(preset);
    if (preset !== "custom") {
      setDateRange(rangeForPreset(preset));
    }
  };
  const [shiftType, setShiftType] = useState<string>("all");
  const [isGenerating, setIsGenerating] = useState(false);

  // When report type changes, reset shift type default appropriately
  useEffect(() => {
    if (reportType === "posting-sheet") {
      setShiftType("day");
    } else if (reportType === "planned-vs-actuals" || reportType === "timesheet") {
      setShiftType("all");
    }
  }, [reportType]);

  const ctaLabel = "Download";

  const successMessage = useMemo(() => {
    if (!reportType) return "Report ready";
    if (reportType === "planned-vs-actuals") {
      return `Planned vs Actuals downloaded for ${scope.label}`;
    }
    if (reportType === "timesheet") {
      return `Timesheet downloaded for ${scope.label}`;
    }
    return `Posting Sheet downloaded for ${scope.label}`;
  }, [reportType, scope]);

  const canSubmit = reportType !== null && (
    reportType === "posting-sheet"
      ? singleDate !== undefined
      : dateRange?.from !== undefined && dateRange?.to !== undefined
  );

  const handleSubmit = () => {
    if (!reportType || !canSubmit) return;
    setIsGenerating(true);
    const filename = filenameFor(reportType, scope, dateRange, singleDate, shiftType);
    // Stubbed — simulate a short generation delay
    window.setTimeout(() => {
      setIsGenerating(false);
      toast.success(successMessage, {
        description: `File: ${filename}`,
      });
      onDownloadComplete?.({ reportType, scope, filename });
    }, 700);
  };

  const handlePreview = () => {
    if (!reportType || !canSubmit) return;
    const params = new URLSearchParams({
      preview: "1",
      type: reportType,
      scopeKind: scope.kind,
      scopeId: scope.id,
      scopeLabel: scope.label,
      shift: shiftType,
    });
    if (scope.kind === "site" && scope.region) params.set("scopeRegion", scope.region);
    if (scope.kind === "roster" && scope.parentSite) params.set("scopeParent", scope.parentSite);
    if (reportType === "posting-sheet" && singleDate) {
      params.set("date", singleDate.toISOString());
    } else if (dateRange?.from && dateRange?.to) {
      params.set("dateFrom", dateRange.from.toISOString());
      params.set("dateTo", dateRange.to.toISOString());
    }
    const url = `${window.location.pathname}?${params.toString()}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const needsDateRange = reportType === "planned-vs-actuals" || reportType === "timesheet";
  const needsSingleDate = reportType === "posting-sheet";

  return (
    <div className="flex flex-col gap-md">
      <ScopeSummary scope={scope} />

      <ReportTypePicker value={reportType} onChange={setReportType} />

      {/* Filters block — appears after report type is chosen */}
      {reportType && (
        <div className="rounded-lg border border-greyscale-border bg-white p-md space-y-sm">
          <p className="text-sm font-semibold text-greyscale-text-title">
            Filters
          </p>

          {needsDateRange && (
            <div>
              <Label className="text-sm font-semibold text-greyscale-text-title mb-xs block">
                Date range
              </Label>
              <div className="flex flex-col sm:flex-row gap-xs">
                <Select value={datePreset} onValueChange={(v) => handlePresetChange(v as DatePreset)}>
                  <SelectTrigger className="w-full sm:w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DATE_PRESET_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {datePreset === "custom" && (
                  <div className="flex-1">
                    <DateRangePicker
                      dateRange={dateRange}
                      onDateRangeChange={setDateRange}
                      placeholder="Pick a date range"
                    />
                  </div>
                )}
              </div>
              <p className="text-xs text-greyscale-text-caption mt-xxxs">
                {datePreset === "custom"
                  ? "Pick any range."
                  : `${formatShortDate(dateRange?.from)} – ${formatShortDate(dateRange?.to)}`}
              </p>
            </div>
          )}

          {needsSingleDate && (
            <div>
              <Label className="text-sm font-semibold text-greyscale-text-title mb-xs block">
                Date
              </Label>
              <div className="w-full sm:w-[220px]">
                <DatePicker
                  date={singleDate}
                  onDateChange={setSingleDate}
                  placeholder="Pick a date"
                />
              </div>
              <p className="text-xs text-greyscale-text-caption mt-xxxs">
                Posting Sheet covers a single day.
              </p>
            </div>
          )}

          <ShiftTypeSelector
            value={shiftType}
            onChange={setShiftType}
            allowAll={reportType !== "posting-sheet"}
          />
        </div>
      )}

      {/* Loading skeleton while "generating" */}
      {isGenerating && (
        <div className="space-y-xs">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      )}

      {/* Action row — stacks on mobile, right-aligned on sm+ */}
      <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-sm pt-md mt-xs border-t border-greyscale-border">
        {onCancel && (
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={isGenerating}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
        )}
        <Button
          variant="outline"
          onClick={handlePreview}
          disabled={!canSubmit || isGenerating}
          leftIcon={<ExternalLink size={16} />}
          className="w-full sm:w-auto"
        >
          Preview
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit || isGenerating}
          leftIcon={<Download size={16} />}
          className="w-full sm:w-auto"
        >
          {isGenerating ? "Generating…" : ctaLabel}
        </Button>
      </div>
    </div>
  );
}

/* ================================================================== */
/*  Dialog wrapper — for contextual entry points (site/roster views)   */
/* ================================================================== */

export interface ReportWizardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scope: ReportScope;
}

export function ReportWizardDialog({ open, onOpenChange, scope }: ReportWizardDialogProps) {
  const contextLabel = scope.kind === "site" ? "site" : "roster";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw_-_1rem)] max-w-none sm:w-full sm:max-w-[560px] max-h-[90vh] overflow-y-auto">
        <DialogHeader className="mb-md">
          <DialogTitle className="text-lg font-semibold text-greyscale-text-title">
            Download Report
          </DialogTitle>
          <DialogDescription className="text-sm text-greyscale-text-body">
            Pre-scoped to this {contextLabel}. Pick a report type and set filters.
          </DialogDescription>
        </DialogHeader>
        <ReportWizardBody
          scope={scope}
          onCancel={() => onOpenChange(false)}
          onDownloadComplete={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}
